/**
 * Writing generated motion into a line (W2-11 batch-tools): shared by Motion path, Clone to lines and Flatten.
 *
 * `writeSlice` puts a run of keys (absolute beats) on (line, property, layer): an existing track keeps its curve
 * outside the run exactly (shape-preserving splits at the run's ends, core/commands motionTime `replaceSpan`); an
 * absent track is created and gets the beat-0 Stay key of the lead-in invariant when the run starts later
 * (core/commands motionTrack `commitKeys`; additive layers > 0 hold 0 there, so they add nothing before the run).
 * `alignXy` keeps the two position axes of X/Y-linked lines keyed at the same beats afterwards.
 */

import { LINEAR } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import {
  alignXyBeats,
  commitKeys,
  createTrack,
  curveCtx,
  EXACT_SPLIT,
  MAX_UI_LAYERS,
  newRec,
  ownerInfo,
  readKeys,
  replaceSpan,
  type KeyRec,
  type SliceKey,
  type SplitReport,
  type TrackInfo,
} from '../commands/kit';
import type { KeyId, LineId, LineProp, TrackValue } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import { toolReason, TOOLS_MSG } from './common';

export interface SliceWrite {
  readonly info: TrackInfo;
  /** Ids of the keys at the slice's beats, in order. */
  readonly ids: KeyId[];
  readonly report: SplitReport;
  readonly created: boolean;
}

/** A slice of keys from values at beats, joined by one ease (linear by default). */
export function sliceOf(beats: readonly Beat[], values: readonly TrackValue[], ease: EaseValue = LINEAR): SliceKey[] {
  return beats.map((t, i) => {
    const v = values[i] as TrackValue;
    return { t, vIn: v, vOut: v, ease, link: 0 };
  });
}

/** Writes `slice` on (line, prop, layer) (see the file header). Fails when the line does not exist. */
export function writeSlice(
  tx: TxBuilder,
  line: LineId,
  prop: LineProp,
  layer: number,
  slice: readonly SliceKey[],
  unlinked: Set<number>,
): SliceWrite {
  const found = ownerInfo(tx, { kind: 'line', line, prop, layer });
  if (found === null) tx.fail(toolReason(TOOLS_MSG.lineMissing));
  let report: SplitReport = EXACT_SPLIT;
  let keys: KeyRec[];
  let info = found.info;
  if (found.exists) {
    keys = readKeys(tx, info.track.id);
    report = replaceSpan(keys, slice, curveCtx(tx, info), unlinked);
  } else {
    info = createTrack(tx, info.track.owner);
    keys = slice.map((s) => newRec(s.t, s.vIn, s.vOut, s.ease, s.link));
  }
  commitKeys(tx, info, keys);
  const wanted = new Set(slice.map((s) => Beats.key(s.t)));
  const ids: KeyId[] = [];
  for (const k of keys) if (k.id !== null && wanted.has(Beats.key(k.t))) ids.push(k.id);
  return { info, ids, report, created: !found.exists };
}

/** Lowest layer index (0..3) free on every one of `props` of the line, or null. */
export function freeCommonLayer(tx: Pick<TxBuilder, 'idx'>, line: LineId, props: readonly LineProp[]): number | null {
  for (let layer = 0; layer < MAX_UI_LAYERS; layer++) {
    if (props.every((p) => tx.idx.track(line, p, layer) === undefined)) return layer;
  }
  return null;
}

/**
 * After writing position keys inside `spans` on an X/Y-linked line: both axes get keys at the same beats there
 * (shape-preserving; an absent axis is created as a constant 0). Returns the worst split of the partner axis.
 */
export function alignXy(
  tx: TxBuilder,
  line: LineId,
  layer: number,
  spans: readonly (readonly [Beat, Beat])[],
  edited: readonly LineProp[],
  unlinked: Set<number>,
): SplitReport {
  if (tx.doc.lines.get(line)?.editor.xyLinked !== true) return EXACT_SPLIT;
  const r = alignXyBeats(
    tx,
    {
      line,
      layer,
      spans: spans.map(([s, e]) => [s, e]),
      beats: new Map([[Beats.key(Beats.ZERO), Beats.ZERO]]),
      edited: new Set(edited),
    },
    unlinked,
  );
  return r.partner;
}
